/**
 * RootRedirect — group-aware boot-time routing attractor for the SPA root.
 *
 * BUG-20260626-143616: App root '/' and catch-all '*' unconditionally redirected
 * every user to /dashboard regardless of role. This component replaces those two
 * static <Navigate> elements so the correct landing route is re-derived on every
 * app boot / reload / returning session — not only at fresh form-login.
 *
 * Design: mirrors ParentGuard.tsx exactly (isLoading spinner gate, unauthenticated
 * redirect to LOGIN, authenticated redirect via getPostLoginRoute). The component
 * is a pure redirector (no Outlet) because the root must choose among multiple
 * destinations for all roles.
 *
 * Implements TO-LL-005 (isLoading gate), TO-LL-013 (ROUTES constants, no literals).
 * Addresses M-1 from reviewer: includes emailVerified gate for parity with AuthGuard.
 */

// BUG-20260626-143616: New boot-time group-aware root redirect component - 2026-06-26
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getPostLoginRoute, ROUTES } from '@/constants/routes';

/**
 * RootRedirect — determines the correct landing route for the current auth state.
 *
 * Used as the element for the root '/' route and the catch-all '*' route in App.tsx.
 * Delegates the group->route decision entirely to getPostLoginRoute(), which is the
 * single source of the parent/student routing fork (TO-LL-013).
 */
export function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();

  // TO-LL-005: isLoading gate prevents any redirect before the Zustand auth store
  // has fully rehydrated (user.groups populated). Spinner markup copied verbatim
  // from ParentGuard.tsx:46-55 and AuthGuard.tsx:15-24.
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Unauthenticated visitor: send directly to login (one hop, vs. the prior
  // two-hop /dashboard -> AuthGuard -> /login path).
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // M-1 (reviewer): authenticated-but-unverified users must reach /verify-email.
  // Without this gate a parent with emailVerified===false would bypass AuthGuard's
  // check and land on /parent (ParentGuard has no emailVerified check).
  if (user?.emailVerified === false) {
    return <Navigate to={ROUTES.VERIFY_EMAIL} replace />;
  }

  // Authenticated and verified: delegate the parent/student fork to the existing
  // pure helper. user?.groups handles legacy users (no groups key) safely — the
  // helper coalesces undefined to [] and returns STUDENT_HOME.
  return <Navigate to={getPostLoginRoute(user?.groups)} replace />;
}
